import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, Trash2, Users, AlertTriangle, CheckCircle, Trash, Key, Activity, RefreshCw, Download, BarChart3, DollarSign, Search as SearchIcon, User as UserIcon } from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts'
import * as adminApi from '../api/admin'
import { useAuth } from '../contexts/AuthContext'

type AdminTab = 'metrics' | 'user_detail' | 'users' | 'config' | 'data'

function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

function ApiKeyManager({ setSuccess, setError }: { setSuccess: (msg: string) => void; setError: (msg: string) => void }) {
  const [deepseekKey, setDeepseekKey] = useState('')
  const [ncbiKey, setNcbiKey] = useState('')
  const queryClient = useQueryClient()

  const { data: keyStatus, isLoading } = useQuery({
    queryKey: ['admin-api-keys'],
    queryFn: adminApi.getApiKeyStatus,
  })

  const updateKeysMutation = useMutation({
    mutationFn: adminApi.updateApiKeys,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-api-keys'] })
      setDeepseekKey('')
      setNcbiKey('')
      setSuccess('API keys updated successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to update API keys'),
  })

  const handleSave = () => {
    const data: { deepseek_api_key?: string; ncbi_api_key?: string } = {}
    if (deepseekKey.trim()) data.deepseek_api_key = deepseekKey.trim()
    if (ncbiKey.trim()) data.ncbi_api_key = ncbiKey.trim()
    if (Object.keys(data).length > 0) {
      updateKeysMutation.mutate(data)
    }
  }

  const handleClearDeepseek = () => {
    updateKeysMutation.mutate({ deepseek_api_key: '' })
  }

  const handleClearNcbi = () => {
    updateKeysMutation.mutate({ ncbi_api_key: '' })
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Key className="w-5 h-5" />
        API Keys
      </h2>

      {isLoading ? (
        <div className="text-slate-500">Loading API key status...</div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-sm">
            <span className={`px-2 py-1 rounded ${keyStatus?.deepseek_api_key_set ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              DeepSeek: {keyStatus?.deepseek_api_key_set ? 'Configured' : 'Not Set'}
            </span>
            <span className={`px-2 py-1 rounded ${keyStatus?.ncbi_api_key_set ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              NCBI: {keyStatus?.ncbi_api_key_set ? 'Configured' : 'Not Set'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">DeepSeek API Key</label>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={deepseekKey}
                  onChange={(e) => setDeepseekKey(e.target.value)}
                  placeholder={keyStatus?.deepseek_api_key_set ? '•••••••• (set)' : 'Enter API key'}
                  className="flex-1 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {keyStatus?.deepseek_api_key_set && (
                  <button
                    onClick={handleClearDeepseek}
                    disabled={updateKeysMutation.isPending}
                    className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">NCBI API Key</label>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={ncbiKey}
                  onChange={(e) => setNcbiKey(e.target.value)}
                  placeholder={keyStatus?.ncbi_api_key_set ? '•••••••• (set)' : 'Enter API key'}
                  className="flex-1 px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {keyStatus?.ncbi_api_key_set && (
                  <button
                    onClick={handleClearNcbi}
                    disabled={updateKeysMutation.isPending}
                    className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={updateKeysMutation.isPending || (!deepseekKey.trim() && !ncbiKey.trim())}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {updateKeysMutation.isPending ? 'Saving...' : 'Save API Keys'}
          </button>
        </div>
      )}
    </div>
  )
}

export default function AdminPage() {
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState<AdminTab>('metrics')
  const [userSearch, setUserSearch] = useState('')
  const debouncedUserSearch = useDebounced(userSearch, 250)
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['admin-config'],
    queryFn: adminApi.getConfig,
    enabled: isAdmin,
  })

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['admin-sessions'],
    queryFn: () => adminApi.listSessions(),
    enabled: isAdmin,
  })

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users', debouncedUserSearch],
    queryFn: () => adminApi.listUsers({ q: debouncedUserSearch }),
    enabled: isAdmin,
  })

  const deleteUserMutation = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSuccess('User deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to delete user'),
  })

  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminApi.getPlanLimits,
    enabled: isAdmin,
  })

  const updatePlanMutation = useMutation({
    mutationFn: ({ planName, limits }: { planName: string; limits: { daily_limit: number; monthly_limit: number } }) =>
      adminApi.updatePlanLimit(planName, limits),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      setSuccess('Plan limits updated successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to update plan limits'),
  })

  const updateUserPlanMutation = useMutation({
    mutationFn: ({ userId, plan }: { userId: string; plan: string }) =>
      adminApi.updateUserPlan(userId, plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSuccess('User plan updated successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to update user plan'),
  })

  const updateConfigMutation = useMutation({
    mutationFn: adminApi.updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-config'] })
      setSuccess('Configuration updated successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to update config'),
  })

  const deleteSessionMutation = useMutation({
    mutationFn: adminApi.deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sessions'] })
      setSuccess('Session deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
  })

  const clearCacheMutation = useMutation({
    mutationFn: () => adminApi.clearCache('*'),
    onSuccess: (data) => {
      setSuccess(data.message)
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to clear cache'),
  })

  const handleConfigUpdate = (field: string, value: any) => {
    updateConfigMutation.mutate({ [field]: value })
  }

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto mt-16 p-6 bg-white rounded-lg shadow">
        <div className="flex items-center gap-2 mb-6">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <h1 className="text-2xl font-bold text-slate-800">Access Denied</h1>
        </div>
        <p className="text-slate-600">
          You don't have permission to access the admin panel.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-slate-800">Admin Panel</h1>
        </div>
      </div>

      {success && (
        <div className="p-3 bg-green-50 text-green-700 rounded flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {success}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      <AdminTabBar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'metrics' && (
        <UserMetricsSection isAdmin={isAdmin} />
      )}

      {activeTab === 'user_detail' && (
        <UserDetailSection isAdmin={isAdmin} />
      )}

      {activeTab === 'config' && (<>

      {/* Configuration Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Application Configuration
        </h2>
        
        {configLoading ? (
          <div className="text-slate-500">Loading configuration...</div>
        ) : config ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConfigField
              label="DeepSeek Model"
              value={config.deepseek_model}
              onChange={(v) => handleConfigUpdate('deepseek_model', v)}
            />
            <ConfigField
              label="Temperature"
              value={config.temperature}
              type="number"
              step="0.1"
              onChange={(v) => handleConfigUpdate('temperature', parseFloat(v))}
            />
            <ConfigField
              label="Max Tokens"
              value={config.max_tokens}
              type="number"
              onChange={(v) => handleConfigUpdate('max_tokens', parseInt(v))}
            />
            <ConfigField
              label="Session Retention (days)"
              value={config.session_retention_days}
              type="number"
              onChange={(v) => handleConfigUpdate('session_retention_days', parseInt(v))}
            />
            <ConfigField
              label="PubMed Batch Size"
              value={config.pubmed_batch_size}
              type="number"
              onChange={(v) => handleConfigUpdate('pubmed_batch_size', parseInt(v))}
            />
            <ConfigField
              label="PubMed Max Retries"
              value={config.pubmed_max_retries}
              type="number"
              onChange={(v) => handleConfigUpdate('pubmed_max_retries', parseInt(v))}
            />
            <ConfigField
              label="PubMed Retry Delay (s)"
              value={config.pubmed_retry_delay}
              type="number"
              onChange={(v) => handleConfigUpdate('pubmed_retry_delay', parseInt(v))}
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.disable_thinking}
                onChange={(e) => handleConfigUpdate('disable_thinking', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-700">Disable Thinking</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.pubmed_lite_mode_default}
                onChange={(e) => handleConfigUpdate('pubmed_lite_mode_default', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-700">Lite Mode Default</span>
            </div>
            <ConfigField
              label="AI batch concurrency (parallel DeepSeek calls)"
              value={config.ai_batch_concurrency}
              type="number"
              onChange={(v) => {
                const n = parseInt(v)
                if (!Number.isNaN(n) && n >= 1 && n <= 32) handleConfigUpdate('ai_batch_concurrency', n)
              }}
            />
            <ConfigField
              label="AI batch size (articles per DeepSeek call)"
              value={config.ai_batch_size}
              type="number"
              onChange={(v) => {
                const n = parseInt(v)
                if (!Number.isNaN(n) && n >= 10 && n <= 500) handleConfigUpdate('ai_batch_size', n)
              }}
            />
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-600">Table-output merge</label>
              <select
                value={config.ai_table_merge_mode}
                onChange={(e) => handleConfigUpdate('ai_table_merge_mode', e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded text-sm bg-white"
              >
                <option value="python">Concatenate in Python (fast)</option>
                <option value="llm">LLM merge (slower)</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="text-slate-500">No configuration available</div>
        )}
      </div>

      {/* Token Weights Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Token Weights (AI Units per 1000 tokens)
        </h2>
        {configLoading ? (
          <div className="text-slate-500">Loading...</div>
        ) : config ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ConfigField
              label="Cache Hit Weight"
              value={config.cache_hit_weight}
              type="number"
              step="0.01"
              onChange={(v) => {
                const n = parseFloat(v)
                if (!Number.isNaN(n) && n >= 0) handleConfigUpdate('cache_hit_weight', n)
              }}
            />
            <ConfigField
              label="Cache Miss Weight"
              value={config.cache_miss_weight}
              type="number"
              step="0.01"
              onChange={(v) => {
                const n = parseFloat(v)
                if (!Number.isNaN(n) && n >= 0) handleConfigUpdate('cache_miss_weight', n)
              }}
            />
            <ConfigField
              label="Output Weight"
              value={config.output_weight}
              type="number"
              step="0.01"
              onChange={(v) => {
                const n = parseFloat(v)
                if (!Number.isNaN(n) && n >= 0) handleConfigUpdate('output_weight', n)
              }}
            />
          </div>
        ) : (
          <div className="text-slate-500">No configuration available</div>
        )}
        <p className="text-xs text-slate-500 mt-2">
          Effective AI units = (cache_hit × weight + cache_miss × weight + output × weight) / 1000
        </p>
      </div>

      {/* Pricing Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Pricing (per million tokens)
        </h2>
        {configLoading ? (
          <div className="text-slate-500">Loading...</div>
        ) : config ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ConfigField
              label="Cache Hit Price ($/M)"
              value={config.cache_hit_price ?? ''}
              type="number"
              step="0.01"
              onChange={(v) => {
                const n = parseFloat(v)
                handleConfigUpdate('cache_hit_price', Number.isNaN(n) ? null : n)
              }}
            />
            <ConfigField
              label="Cache Miss Price ($/M)"
              value={config.cache_miss_price ?? ''}
              type="number"
              step="0.01"
              onChange={(v) => {
                const n = parseFloat(v)
                handleConfigUpdate('cache_miss_price', Number.isNaN(n) ? null : n)
              }}
            />
            <ConfigField
              label="Output Price ($/M)"
              value={config.output_price ?? ''}
              type="number"
              step="0.01"
              onChange={(v) => {
                const n = parseFloat(v)
                handleConfigUpdate('output_price', Number.isNaN(n) ? null : n)
              }}
            />
          </div>
        ) : (
          <div className="text-slate-500">No configuration available</div>
        )}
        <p className="text-xs text-slate-500 mt-2">
          Enter prices to see cost estimates in the dashboard. Leave blank to disable cost tracking.
        </p>
      </div>

      {/* API Keys Section */}
      <ApiKeyManager setSuccess={setSuccess} setError={setError} />

      {/* Cache Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Trash className="w-5 h-5" />
          Cache Management
        </h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => clearCacheMutation.mutate()}
            disabled={clearCacheMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {clearCacheMutation.isPending ? 'Clearing...' : 'Clear All Cache'}
          </button>
          <span className="text-xs text-slate-500">
            Clears all Redis cached data (search results, iCite metrics, etc.)
          </span>
        </div>
      </div>

      </>)}

      {activeTab === 'users' && (<>

      {/* Plan Management Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Plan Limits
        </h2>

        {plansLoading ? (
          <div className="text-slate-500">Loading plans...</div>
        ) : plansData?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Plan</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Daily Limit (AI units)</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Monthly Limit (AI units)</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {plansData.map((plan) => (
                  <PlanLimitRow
                    key={plan.plan_name}
                    plan={plan}
                    onUpdate={(limits) => updatePlanMutation.mutate({ planName: plan.plan_name, limits })}
                    isPending={updatePlanMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-500 mt-2">
              1 AI unit = 1000 tokens. Limits are enforced per user.
            </p>
          </div>
        ) : (
          <div className="text-slate-500">No plans configured</div>
        )}
      </div>

      {/* Users Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Registered Users ({usersData?.total || 0})
        </h2>

        <div className="mb-4 relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {usersLoading ? (
          <div className="text-slate-500">Loading users...</div>
        ) : usersData?.users?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">User ID</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Email</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Name</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Plan</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Status</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Created</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {usersData.users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{user.user_id}</td>
                    <td className="px-4 py-2 text-xs text-slate-700">{user.email}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{user.name || 'N/A'}</td>
                    <td className="px-4 py-2">
                      <select
                        value={user.plan}
                        onChange={(e) => updateUserPlanMutation.mutate({ userId: user.user_id, plan: e.target.value })}
                        disabled={updateUserPlanMutation.isPending}
                        className="text-xs border rounded px-2 py-1 bg-white capitalize"
                      >
                        <option value="basic">Basic</option>
                        <option value="plus">Plus</option>
                        <option value="extra">Extra</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete user ${user.email}? This will also delete all their sessions and datasets.`)) {
                            deleteUserMutation.mutate(user.user_id)
                          }
                        }}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-slate-500">
            {debouncedUserSearch ? `No users match "${debouncedUserSearch}"` : 'No registered users'}
          </div>
        )}
      </div>

      {/* Sessions Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Active Sessions ({sessionsData?.total || 0})
        </h2>
        
        {sessionsLoading ? (
          <div className="text-slate-500">Loading sessions...</div>
        ) : sessionsData?.sessions?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Session ID</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Search ID</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Created</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Last Accessed</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Expires</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sessionsData.sessions.map((session) => (
                  <tr key={session.session_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{session.session_id}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{session.search_id || 'N/A'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(session.created_at)}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(session.last_accessed)}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(session.expires_at)}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => deleteSessionMutation.mutate(session.session_id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Delete session"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-slate-500">No active sessions</div>
        )}
      </div>

      </>)}

      {activeTab === 'data' && (<>

      {/* PubMed Cache Section */}
      <PubMedCacheSection isAdmin={isAdmin} setError={setError} setSuccess={setSuccess} />

      {/* SJR re-annotation */}
      <SjrReannotationSection isAdmin={isAdmin} setError={setError} setSuccess={setSuccess} />

      </>)}
    </div>
  )
}

function UserDetailSection({ isAdmin }: { isAdmin: boolean }) {
  const [range, setRange] = useState<adminApi.MetricsRange>('30d')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, 250)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const { data: searchResults, isFetching: searchFetching } = useQuery({
    queryKey: ['admin-user-search', debouncedSearch],
    queryFn: () => adminApi.listUsers({ q: debouncedSearch, limit: 10 }),
    enabled: isAdmin && debouncedSearch.trim().length > 0,
    staleTime: 5_000,
  })

  const { data: detail, isLoading: detailLoading, isFetching: detailFetching } = useQuery({
    queryKey: ['admin-user-detail', selectedUserId, range],
    queryFn: () => adminApi.getUserDetail(selectedUserId!, range),
    enabled: isAdmin && !!selectedUserId,
    staleTime: 0,
  })

  const dailyActivity = useMemo(() => (detail?.daily || []).map(d => ({
    date: d.date,
    Searches: d.searches,
    BERTopic: d.bertopic,
    Semantic: d.semantic,
    AI: d.ai_batch,
    LLM: d.llm_requests,
  })), [detail?.daily])

  const dailyTokens = useMemo(() => (detail?.daily || []).map(d => ({
    date: d.date,
    'Cache Hit': d.cache_hit_tokens,
    'Cache Miss': d.cache_miss_tokens,
    Output: d.output_tokens,
  })), [detail?.daily])

  const caps = detail?.usage_caps
  const totals = detail?.totals

  return (
    <div className="space-y-4">
      {/* Search + range */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            User Detail
          </h2>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as adminApi.MetricsRange)}
            className="px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>

        <div className="relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search user by email or name…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {debouncedSearch && (
          <div className="mt-3 max-w-md border rounded divide-y">
            {searchFetching ? (
              <div className="px-3 py-2 text-sm text-slate-500">Searching…</div>
            ) : searchResults?.users?.length ? (
              searchResults.users.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => setSelectedUserId(u.user_id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between ${
                    selectedUserId === u.user_id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div>
                    <div className="text-slate-800">{u.email}</div>
                    {u.name && <div className="text-xs text-slate-500">{u.name}</div>}
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{u.plan}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">No matches</div>
            )}
          </div>
        )}
      </div>

      {/* Detail */}
      {selectedUserId ? (
        detailLoading ? (
          <div className="bg-white rounded-lg shadow p-6 text-slate-500">Loading user details…</div>
        ) : detail ? (
          <>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-lg font-semibold text-slate-800">{detail.email}</div>
                  <div className="text-sm text-slate-500">{detail.name || '—'}</div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                    detail.plan === 'basic' ? 'bg-gray-100 text-gray-700' :
                    detail.plan === 'plus' ? 'bg-blue-100 text-blue-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>{detail.plan}</span>
                  <span className={`px-2 py-1 rounded text-xs ${detail.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {detail.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs text-slate-500">
                    Created: {formatDate(detail.created_at || undefined)}
                  </span>
                  <span className="text-xs text-slate-500">
                    Last active: {formatDate(detail.last_active || undefined)}
                  </span>
                </div>
              </div>

              {detailFetching && <div className="mt-2 text-xs text-slate-500">Refreshing…</div>}

              {/* Totals KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">
                <KpiCard label="Searches" value={totals?.search_count} loading={false} />
                <KpiCard label="BERTopic" value={totals?.bertopic_count} loading={false} />
                <KpiCard label="Semantic" value={totals?.semantic_count} loading={false} />
                <KpiCard label="AI batch" value={totals?.ai_batch_count} loading={false} />
                <KpiCard label="LLM requests" value={totals?.llm_requests} loading={false} />
                <KpiCard label="LLM tokens" value={totals?.llm_tokens} loading={false} format="compact" />
              </div>

              {/* Token + cost panel */}
              {totals && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <div className="text-xs text-slate-500">Cache Hit</div>
                      <div className="text-sm font-medium text-slate-800">{totals.cache_hit_tokens.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Cache Miss</div>
                      <div className="text-sm font-medium text-slate-800">{totals.cache_miss_tokens.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Input total</div>
                      <div className="text-sm font-medium text-slate-800">
                        {(totals.cache_hit_tokens + totals.cache_miss_tokens).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Output</div>
                      <div className="text-sm font-medium text-slate-800">{totals.output_tokens.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Est. cost</div>
                      <div className="text-sm font-medium text-green-700">
                        {totals.estimated_cost != null ? `$${totals.estimated_cost.toFixed(2)}` : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan caps */}
              {caps && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Daily plan usage</span>
                      <span className="text-slate-800 font-medium">
                        {caps.used_daily.toLocaleString()} / {caps.daily_limit.toLocaleString()} AI units
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${Math.min(100, (caps.used_daily / Math.max(1, caps.daily_limit)) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Monthly plan usage</span>
                      <span className="text-slate-800 font-medium">
                        {caps.used_monthly.toLocaleString()} / {caps.monthly_limit.toLocaleString()} AI units
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500"
                        style={{ width: `${Math.min(100, (caps.used_monthly / Math.max(1, caps.monthly_limit)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Activity chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Daily activity</h3>
              {dailyActivity.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No activity in this range</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={dailyActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Searches" stackId="a" fill="#3b82f6" />
                    <Bar dataKey="BERTopic" stackId="a" fill="#8b5cf6" />
                    <Bar dataKey="Semantic" stackId="a" fill="#10b981" />
                    <Bar dataKey="AI" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="LLM" stackId="a" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Token chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Daily token usage</h3>
              {dailyTokens.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No usage in this range</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={dailyTokens}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Cache Hit" stackId="t" fill="#10b981" />
                    <Bar dataKey="Cache Miss" stackId="t" fill="#f59e0b" />
                    <Bar dataKey="Output" stackId="t" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-slate-500">Could not load user.</div>
        )
      ) : (
        <div className="bg-white rounded-lg shadow p-6 text-slate-500">
          Search for a user above to see their activity and token usage over time.
        </div>
      )}
    </div>
  )
}


function AdminTabBar({ activeTab, onChange }: { activeTab: AdminTab; onChange: (t: AdminTab) => void }) {
  const tabs: { id: AdminTab; label: string }[] = [
    { id: 'metrics', label: 'Metrics' },
    { id: 'user_detail', label: 'User Detail' },
    { id: 'users', label: 'Users & Plans' },
    { id: 'config', label: 'Configuration' },
    { id: 'data', label: 'PubMed Data' },
  ]
  return (
    <nav className="flex gap-6 border-b border-slate-200">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
            activeTab === t.id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </nav>
  )
}

function PubMedCacheSection({
  isAdmin, setError, setSuccess,
}: { isAdmin: boolean; setError: (m: string) => void; setSuccess: (m: string) => void }) {
  const [mode, setMode] = useState<'year' | 'files'>('year')
  const [year, setYear] = useState<string>(String(new Date().getFullYear()))
  const [yearTo, setYearTo] = useState<string>('')
  const [filesCount, setFilesCount] = useState<string>('200')
  const queryClient = useQueryClient()

  const { data: jobs } = useQuery({
    queryKey: ['admin-prefetch-jobs'],
    queryFn: adminApi.listPrefetchJobs,
    enabled: isAdmin,
    staleTime: 0,
    refetchInterval: (q: any) => {
      const data = q?.state?.data as adminApi.PrefetchJob[] | undefined
      return data?.some(j => j.status === 'running' || j.status === 'pending') ? 5000 : false
    },
  })

  const { data: byYear } = useQuery({
    queryKey: ['admin-articles-by-year'],
    queryFn: adminApi.getArticlesByYear,
    enabled: isAdmin,
    staleTime: 0,
    refetchInterval: () => {
      // Refresh while any prefetch is running, so the chart grows live.
      const jobsData = (queryClient.getQueryData(['admin-prefetch-jobs']) as adminApi.PrefetchJob[] | undefined)
      return jobsData?.some(j => j.status === 'running' || j.status === 'pending') ? 10000 : false
    },
  })

  const startMutation = useMutation({
    mutationFn: (vars: { year?: number; yearTo?: number; filesCount?: number; baselineYear?: number }) =>
      adminApi.startPrefetch(vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-prefetch-jobs'] })
      setSuccess(`Prefetch started for year ${year}`)
      setTimeout(() => setSuccess(''), 4000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to start prefetch'),
  })

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => adminApi.cancelPrefetch(jobId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-prefetch-jobs'] }),
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to cancel prefetch'),
  })

  const handleStart = () => {
    if (mode === 'files') {
      const n = parseInt(filesCount, 10)
      if (Number.isNaN(n) || n < 1) return
      startMutation.mutate({ filesCount: n })
      return
    }
    const y = parseInt(year, 10)
    if (Number.isNaN(y)) return
    const yt = yearTo.trim() ? parseInt(yearTo, 10) : undefined
    if (yt !== undefined && (Number.isNaN(yt) || yt < y)) return
    startMutation.mutate({ year: y, yearTo: yt })
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Download className="w-5 h-5" />
        PubMed Cache (FTP prefetch)
      </h2>

      <BaselineCoverageCard isAdmin={isAdmin} />

      <div className="mb-4">
        <div className="flex gap-4 mb-3 text-sm">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={mode === 'year'}
              onChange={() => setMode('year')}
              className="accent-blue-600"
            />
            By publication year
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={mode === 'files'}
              onChange={() => setMode('files')}
              className="accent-blue-600"
            />
            By file count (bulk; no year filter)
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {mode === 'year' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Year from</label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-28 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Year to <span className="text-slate-400">(optional)</span>
                </label>
                <input
                  type="number"
                  value={yearTo}
                  placeholder={year}
                  onChange={(e) => setYearTo(e.target.value)}
                  className="w-28 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Files to fetch (top-N, newest first)
              </label>
              <input
                type="number"
                value={filesCount}
                min={1}
                max={5000}
                onChange={(e) => setFilesCount(e.target.value)}
                className="w-32 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                Each file ≈ 30 k articles, ~30–50 MB compressed.
              </p>
            </div>
          )}
          <button
            onClick={handleStart}
            disabled={startMutation.isPending}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {startMutation.isPending ? 'Starting…' : 'Start'}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Downloads PubMed baseline XML files from
        <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded">ftp.ncbi.nlm.nih.gov</code>
        in descending order, parses each one, and inserts articles published in the chosen year.
        No API rate limit, no key required. The baseline year is auto-detected
        from the FTP listing (NCBI keeps only the most recent one online,
        currently ~2026); recent target years (2024–2026) finish fastest.
      </p>

      {/* Articles-per-year chart */}
      {byYear && byYear.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-slate-700 mb-2">
            Cached articles by publication year
            <span className="ml-2 text-xs text-slate-500">
              ({byYear.reduce((s, p) => s + p.count, 0).toLocaleString()} total)
            </span>
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byYear}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : String(v)} />
              <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {!jobs || jobs.length === 0 ? (
        <div className="text-slate-500 text-sm">No prefetch jobs yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-slate-600 font-medium">Year</th>
                <th className="px-3 py-2 text-left text-slate-600 font-medium">Status</th>
                <th className="px-3 py-2 text-right text-slate-600 font-medium">Files</th>
                <th className="px-3 py-2 text-right text-slate-600 font-medium">Articles inserted</th>
                <th className="px-3 py-2 text-right text-slate-600 font-medium">Skipped (already cached)</th>
                <th className="px-3 py-2 text-right text-slate-600 font-medium">Downloaded</th>
                <th className="px-3 py-2 text-left text-slate-600 font-medium">Progress</th>
                <th className="px-3 py-2 text-left text-slate-600 font-medium">Started</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map((j) => {
                const pct = j.files_total_est && j.files_total_est > 0
                  ? Math.min(100, Math.round((j.files_processed / j.files_total_est) * 100))
                  : 0
                return (
                  <tr key={j.job_id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-xs text-slate-700 font-medium">
                      {j.files_count
                        ? `Top ${j.files_count} files`
                        : j.year_to && j.year_to !== j.year
                        ? `${j.year}–${j.year_to}`
                        : j.year}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${prefetchStatusClass(j.status)}`}>{j.status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700 text-right">
                      {j.files_processed} / {j.files_total_est ?? '?'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700 text-right">{j.articles_inserted.toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 text-right">{j.articles_skipped.toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 text-right">{formatBytes(j.bytes_downloaded)}</td>
                    <td className="px-3 py-2 w-40">
                      {j.status === 'running' ? (
                        <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(j.started_at || undefined)}</td>
                    <td className="px-3 py-2">
                      {(j.status === 'running' || j.status === 'pending') && (
                        <button
                          onClick={() => cancelMutation.mutate(j.job_id)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SjrReannotationSection({
  isAdmin, setError, setSuccess,
}: { isAdmin: boolean; setError: (m: string) => void; setSuccess: (m: string) => void }) {
  const queryClient = useQueryClient()

  const { data: status } = useQuery({
    queryKey: ['admin-sjr-reannotate'],
    queryFn: adminApi.getSjrReannotationStatus,
    enabled: isAdmin,
    staleTime: 0,
    refetchInterval: (q: any) => {
      const data = q?.state?.data as adminApi.SjrReannotationStatus | undefined
      return data?.status === 'running' ? 3000 : false
    },
  })

  const startMutation = useMutation({
    mutationFn: adminApi.startSjrReannotation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sjr-reannotate'] })
      setSuccess('SJR re-annotation started')
      setTimeout(() => setSuccess(''), 4000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to start re-annotation'),
  })

  const processed = status?.processed || 0
  const updated = status?.updated || 0
  const total = status?.total || 0
  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0
  const running = status?.status === 'running'

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-slate-800 mb-2">SJR (Scimago) re-annotation</h2>
      <p className="text-xs text-slate-500 mb-4">
        New articles are annotated with quartile / SJR score / h-index automatically when
        they're inserted — both from live PubMed searches and from FTP baseline prefetches.
        Run this re-annotation <strong>after the SJR CSV file is updated</strong> (typically
        once a year) to refresh values for every article already in the cache. Idempotent;
        rows whose values don't change are skipped.
      </p>

      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={() => startMutation.mutate()}
          disabled={!isAdmin || running || startMutation.isPending}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {running ? 'Running…' : 'Re-annotate all articles'}
        </button>
        <span className="text-xs text-slate-500">
          Status: <span className="font-mono">{status?.status || 'idle'}</span>
        </span>
      </div>

      {(running || (status?.status === 'complete' && total > 0)) && (
        <div>
          <div className="h-2 bg-slate-100 rounded overflow-hidden mb-1">
            <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-slate-600 flex justify-between">
            <span>{processed.toLocaleString()} / {total.toLocaleString()} scanned</span>
            <span>{updated.toLocaleString()} updated</span>
          </div>
        </div>
      )}
    </div>
  )
}

function BaselineCoverageCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient()
  const { data, isFetching, error } = useQuery({
    queryKey: ['admin-baseline-status'],
    queryFn: adminApi.getBaselineStatus,
    enabled: isAdmin,
    staleTime: 60_000,
  })

  if (!isAdmin) return null

  const baseline = data?.baseline_year
  const remote = data?.remote_total
  const downloaded = data?.downloaded ?? 0
  const pct = remote ? Math.min(100, Math.round((downloaded / remote) * 100)) : 0
  const fetchErr = data?.error || (error as any)?.response?.data?.detail

  return (
    <div className="mb-4 p-3 border border-slate-200 rounded bg-slate-50">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-slate-700">
          Baseline coverage{baseline ? ` — ${baseline}` : ''}
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-baseline-status'] })}
          disabled={isFetching}
          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
        >
          {isFetching ? 'Checking…' : 'Refresh'}
        </button>
      </div>
      {fetchErr ? (
        <div className="text-xs text-amber-700">
          Could not reach NCBI FTP ({String(fetchErr).slice(0, 120)}). Local count: <strong>{downloaded.toLocaleString()}</strong> files.
        </div>
      ) : remote == null ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="text-xs text-slate-600 mb-1">
            <strong>{downloaded.toLocaleString()}</strong> of <strong>{remote.toLocaleString()}</strong> baseline files downloaded
            {' '}({pct}%)
            {remote > downloaded && (
              <> — {(remote - downloaded).toLocaleString()} remaining</>
            )}
          </div>
          <div className="h-2 bg-slate-200 rounded overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
          </div>
        </>
      )}
    </div>
  )
}

function prefetchStatusClass(status: string): string {
  if (status === 'complete') return 'bg-green-100 text-green-700'
  if (status === 'error') return 'bg-red-100 text-red-700'
  if (status === 'running' || status === 'pending') return 'bg-blue-100 text-blue-700'
  if (status === 'cancelled') return 'bg-gray-200 text-gray-600'
  return 'bg-gray-100 text-gray-700'
}

function formatBytes(n: number): string {
  if (!n) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}

function UserMetricsSection({ isAdmin }: { isAdmin: boolean }) {
  const [range, setRange] = useState<adminApi.MetricsRange>('30d')
  const queryClient = useQueryClient()

  const { data: overview, isLoading: overviewLoading, isFetching: overviewFetching } = useQuery({
    queryKey: ['admin-metrics-overview', range],
    queryFn: () => adminApi.getMetricsOverview(range),
    enabled: isAdmin,
    staleTime: 0,
  })

  const { data: userMetrics, isLoading: userMetricsLoading, isFetching: userMetricsFetching } = useQuery({
    queryKey: ['admin-metrics-users', range],
    queryFn: () => adminApi.getUserMetrics(range),
    enabled: isAdmin,
    staleTime: 0,
  })

  const { data: recentSearches, isLoading: recentLoading, isFetching: recentFetching } = useQuery({
    queryKey: ['admin-metrics-recent-searches'],
    queryFn: () => adminApi.getRecentSearches(20),
    enabled: isAdmin,
    staleTime: 0,
  })

  const anyFetching = overviewFetching || userMetricsFetching || recentFetching

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-metrics-overview'] })
    queryClient.invalidateQueries({ queryKey: ['admin-metrics-users'] })
    queryClient.invalidateQueries({ queryKey: ['admin-metrics-recent-searches'] })
  }

  const totals = overview?.totals
  const daily = overview?.daily || []

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          User Metrics
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={anyFetching}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            title="Refresh all metrics"
          >
            <RefreshCw className={`w-4 h-4 ${anyFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as adminApi.MetricsRange)}
            className="px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Users (total)" value={totals?.users} loading={overviewLoading} />
        <KpiCard label="Searches" value={totals?.searches} loading={overviewLoading} />
        <KpiCard label="BERTopic analyses" value={totals?.bertopic_analyses} loading={overviewLoading} />
        <KpiCard label="LLM tokens" value={totals?.llm_tokens} loading={overviewLoading} format="compact" />
      </div>

      {/* Token Cost Summary */}
      {overview?.token_costs && (
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <h3 className="text-sm font-medium text-slate-700 mb-3">Token Breakdown & Cost Estimate</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <div className="text-xs text-slate-500">Cache Hit</div>
              <div className="text-sm font-medium text-slate-800">{overview.token_costs.cache_hit_tokens.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Cache Miss</div>
              <div className="text-sm font-medium text-slate-800">{overview.token_costs.cache_miss_tokens.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Input total</div>
              <div className="text-sm font-medium text-slate-800">
                {(overview.token_costs.cache_hit_tokens + overview.token_costs.cache_miss_tokens).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Output</div>
              <div className="text-sm font-medium text-slate-800">{overview.token_costs.output_tokens.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Total</div>
              <div className="text-sm font-medium text-slate-800">{overview.token_costs.total_tokens.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Est. Cost</div>
              <div className="text-sm font-medium text-green-700">
                {overview.token_costs.estimated_cost != null
                  ? `$${overview.token_costs.estimated_cost.toFixed(2)}`
                  : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily activity chart */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-700 mb-2">Daily activity</h3>
        {overviewLoading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : daily.length === 0 ? (
          <div className="text-slate-500 text-sm">No activity in this range.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="searches" stackId="a" fill="#3b82f6" name="Searches" />
              <Bar dataKey="bertopic" stackId="a" fill="#10b981" name="BERTopic" />
              <Bar dataKey="semantic" stackId="a" fill="#06b6d4" name="Semantic" />
              <Bar dataKey="llm_requests" stackId="b" fill="#a855f7" name="LLM requests" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-user table */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-700 mb-2">Per-user totals ({range === 'all' ? 'lifetime' : range})</h3>
        {userMetricsLoading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : !userMetrics || userMetrics.length === 0 ? (
          <div className="text-slate-500 text-sm">No users yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Email</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Searches</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">BERTopic</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Semantic</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">AI batch</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">LLM requests</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Cache Hit</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Cache Miss</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Input total</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Output</th>
                  <th className="px-4 py-2 text-right text-slate-600 font-medium">Est. Cost</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Last active</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {userMetrics.map((u) => (
                  <tr key={u.user_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-xs text-slate-700">{u.email}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{u.search_count}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{u.bertopic_count}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{u.semantic_count}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{u.ai_batch_count}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{u.llm_requests}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{formatCompact(u.cache_hit_tokens)}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{formatCompact(u.cache_miss_tokens)}</td>
                    <td className="px-4 py-2 text-xs text-slate-800 text-right font-medium">{formatCompact(u.cache_hit_tokens + u.cache_miss_tokens)}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right">{formatCompact(u.output_tokens)}</td>
                    <td className="px-4 py-2 text-xs text-green-700 text-right font-medium">
                      {u.estimated_cost != null ? `$${u.estimated_cost.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(u.last_active || undefined)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent searches */}
      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-2">Recent searches</h3>
        {recentLoading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : !recentSearches || recentSearches.length === 0 ? (
          <div className="text-slate-500 text-sm">No searches yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">User</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Query</th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Status</th>
                  <th
                    className="px-4 py-2 text-right text-slate-600 font-medium"
                    title="Total matches reported by PubMed for this query — most are not downloaded"
                  >
                    PubMed total
                  </th>
                  <th
                    className="px-4 py-2 text-right text-slate-600 font-medium"
                    title="Articles actually fetched and stored locally"
                  >
                    Downloaded
                  </th>
                  <th className="px-4 py-2 text-left text-slate-600 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {recentSearches.map((s) => (
                  <tr key={s.search_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-xs text-slate-700">{s.user_email || '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 max-w-xs truncate" title={s.query}>
                      <Link to={`/search?search_id=${s.search_id}`} className="text-blue-600 hover:underline">
                        {s.query}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${statusClass(s.status)}`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 text-right">
                      {s.total_count != null ? s.total_count.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-700 text-right font-medium">
                      {s.downloaded_count != null ? s.downloaded_count.toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function KpiCard({
  label, value, loading, format,
}: { label: string; value?: number; loading?: boolean; format?: 'compact' }) {
  const display = loading ? '…' : value == null ? '—' : format === 'compact' ? formatCompact(value) : value.toLocaleString()
  return (
    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-800 mt-1">{display}</div>
    </div>
  )
}

function formatCompact(n: number): string {
  if (n < 1000) return String(n)
  if (n < 1_000_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  if (n < 1_000_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
}

function statusClass(status: string): string {
  if (status === 'complete') return 'bg-green-100 text-green-700'
  if (status === 'error') return 'bg-red-100 text-red-700'
  if (status === 'running') return 'bg-blue-100 text-blue-700'
  return 'bg-gray-100 text-gray-700'
}

function ConfigField({
  label,
  value,
  type = 'text',
  step,
  onChange,
}: {
  label: string
  value: string | number
  type?: string
  step?: string
  onChange: (value: string) => void
}) {
  const [localValue, setLocalValue] = useState(String(value))
  
  useEffect(() => {
    setLocalValue(String(value))
  }, [value])

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type={type}
        step={step}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => onChange(localValue)}
        className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  )
}

function PlanLimitRow({
  plan,
  onUpdate,
  isPending,
}: {
  plan: adminApi.PlanLimit
  onUpdate: (limits: { daily_limit: number; monthly_limit: number }) => void
  isPending: boolean
}) {
  const [daily, setDaily] = useState(String(plan.daily_limit))
  const [monthly, setMonthly] = useState(String(plan.monthly_limit))

  useEffect(() => {
    setDaily(String(plan.daily_limit))
    setMonthly(String(plan.monthly_limit))
  }, [plan.daily_limit, plan.monthly_limit])

  const handleSave = () => {
    const d = parseInt(daily, 10)
    const m = parseInt(monthly, 10)
    if (!Number.isNaN(d) && !Number.isNaN(m) && d >= 1 && m >= 1 && d <= m) {
      onUpdate({ daily_limit: d, monthly_limit: m })
    }
  }

  const planColors: Record<string, string> = {
    basic: 'bg-gray-100 text-gray-700',
    plus: 'bg-blue-100 text-blue-700',
    extra: 'bg-purple-100 text-purple-700',
  }

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-2">
        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${planColors[plan.plan_name] || 'bg-gray-100 text-gray-700'}`}>
          {plan.plan_name}
        </span>
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          min={1}
          value={daily}
          onChange={(e) => setDaily(e.target.value)}
          className="w-24 px-2 py-1 text-xs border rounded text-right"
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          min={1}
          value={monthly}
          onChange={(e) => setMonthly(e.target.value)}
          className="w-24 px-2 py-1 text-xs border rounded text-right"
        />
      </td>
      <td className="px-4 py-2">
        <button
          onClick={handleSave}
          disabled={isPending}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? 'Saving...' : 'Save'}
        </button>
      </td>
    </tr>
  )
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return 'N/A'
  try {
    return new Date(dateStr).toLocaleString()
  } catch {
    return dateStr
  }
}
